import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import KeyboardAwareNumberInput from '../Shared/KeyboardAwareNumberInput';

/**
 * Reusable Cash Counter Component
 * Displays denomination inputs and calculates total
 */
export default function CashCounter({ theme, denominations, onChange, readOnly = false }) {
  const { t } = useLanguage();

  // Denomination configurations with labels
  const denoms = [
    { key: '1000', label: '$1000', value: 1000 },
    { key: '500', label: '$500', value: 500 },
    { key: '200', label: '$200', value: 200 },
    { key: '100', label: '$100', value: 100 },
    { key: '50', label: '$50', value: 50 },
    { key: '20', label: '$20', value: 20 },
    { key: '10', label: '$10', value: 10 },
    { key: '5', label: '$5', value: 5 },
    { key: '2', label: '$2', value: 2 },
    { key: '1', label: '$1', value: 1 }
  ];

  // Calculate total cash
  const calculateTotal = () => {
    return denoms.reduce((total, denom) => {
      const count = parseInt(denominations[denom.key] || 0);
      return total + (count * denom.value);
    }, 0);
  };

  const handleDenomChange = (key, value) => {
    // Allow empty string, otherwise parse and remove leading zeros
    if (value === '') {
      onChange({
        ...denominations,
        [key]: ''
      });
    } else {
      const count = parseInt(value) || 0;
      onChange({
        ...denominations,
        [key]: Math.max(0, count)
      });
    }
  };

  const total = calculateTotal();

  return (
    <div style={{
      backgroundColor: theme?.colors?.surface || '#fff',
      borderRadius: '8px',
      padding: '1.5rem',
      border: `1px solid ${theme?.colors?.border || '#ddd'}`
    }}>
      <h3 style={{
        margin: '0 0 1rem 0',
        color: theme?.colors?.text || '#000',
        fontSize: '1.1rem',
        fontWeight: 600
      }}>
        {t('cashCount')}
      </h3>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {denoms.map(denom => (
          <div key={denom.key} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            <label style={{
              fontSize: '0.9rem',
              fontWeight: 500,
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {denom.label}
            </label>
            <KeyboardAwareNumberInput
              value={denominations[denom.key]}
              onChange={(value) => handleDenomChange(denom.key, value)}
              options={{ min: 0 }}
              placeholder="0"
              disabled={readOnly}
              style={{
                padding: '0.5rem',
                fontSize: '1rem',
                backgroundColor: readOnly ? (theme?.colors?.background || '#f5f5f5') : (theme?.colors?.surface || '#fff'),
                WebkitAppearance: 'none',
                MozAppearance: 'textfield'
              }}
            />
            <div style={{
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666',
              textAlign: 'right'
            }}>
              ${((parseInt(denominations[denom.key] || 0) * denom.value).toFixed(2))}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        borderTop: `2px solid ${theme?.colors?.border || '#ddd'}`,
        paddingTop: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{
          fontSize: '1.2rem',
          fontWeight: 600,
          color: theme?.colors?.text || '#000'
        }}>
          {t('total')}:
        </span>
        <span style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: theme?.colors?.primary || '#1976d2'
        }}>
          ${total.toFixed(2)}
        </span>
      </div>
    </div>
  );
}
